import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  Grid,
  Stack,
  Autocomplete,
  Chip,
  TextField,
  MenuItem,
} from '@mui/material';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import getFileData from 'src/utils/getFileData';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import {
  useBinarys,
  useOrganizations,
  useValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import CancelButton from '../CancelButton';
import {
  createBinaryCustom,
  mapDocumentReferenceToResource,
  saveDocumentReference,
} from './document';
import { UploadFile } from './UploadFile';
import { Reference } from 'src/careflow/ph-core/fhir-client/resources';

type Props = {
  open: boolean;
  patient: WrappedPatient | null;
  resource?: any;
  createDocumentReference: any;
  refreshDocumentReference: any;
  episodeOfCare?: Reference;
  handleUpdateResource: (data: any) => Promise<any>;
  onCancel: VoidFunction;
  i18n: i18nFunction;
};

type FormValues = {
  file: any;
  name?: string;
  type?: string;
  fileType?: string;
  preview?: string;
  author?: string;
  category?: string[];
  custodian: string;
};

export default function AttachmentAddForm({
  open,
  patient,
  resource,
  createDocumentReference,
  refreshDocumentReference,
  episodeOfCare,
  handleUpdateResource,
  onCancel,
  i18n,
}: Props) {
  const defaultValues = {
    file: '',
    name: '',
    type: '',
    fileType: '',
    preview: '',
    author: '',
    category: [],
    custodian: '',
  };

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const {
    control,
    clearErrors,
    reset,
    watch,
    setError,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (file) {
        setValue(
          'file',
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
      }
    },
    [setValue]
  );

  const fileUp = watch('file');
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();
  const { configurations } = useTenantConfigData();
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [, { create: createBinary }] = useBinarys({ autofetch: false });
  const [organizations] = useOrganizations({ map: OrganizationWrapper });
  const [documentType] = useValueSet('ph-document-type-codes', { map: ValueSetWrapper });
  const [documentCategory] = useValueSet('ph-document-category-codes', { map: ValueSetWrapper });

  useEffect(() => {
    if (fileUp) {
      clearErrors('name');
      setValue('name', getFileData(fileUp).name);
      setValue('fileType', getFileData(fileUp).type);
      setValue('author', authUser.name);
    }
  }, [fileUp]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (!data?.name) {
        setError('name', {
          type: 'manual',
          message: 'File Name is required.',
        });
        return;
      }

      setOpenBackdrop(true);

      const resultBinary = await createBinaryCustom(data, patient, createBinary);
      const mapDocumentReference: any = saveDocumentReference(
        resultBinary,
        data,
        authUser,
        organizations,
        documentCategory,
        documentType,
        patient,
        resource,
        configurations,
        episodeOfCare
      );

      const documentToSave = await createDocumentReference(mapDocumentReference);
      const mapToResource = mapDocumentReferenceToResource(documentToSave, resource);

      if (mapToResource && resource) {
        resource.supportingInfo = mapToResource;
        if (handleUpdateResource !== null) handleUpdateResource(resource);
      }
      refreshDocumentReference();
      enqueueSnackbar('Attachment was created');
      setOpenBackdrop(false);
      onClose();
    } catch (error) {
      enqueueSnackbar('Attachment was not created', { variant: 'error' });
      setOpenBackdrop(false);
      onClose();
    }
  };

  const onClose = () => {
    onCancel();
    reset();
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>{i18n('patients.details.attachments.button', 'crs')}</DialogTitle>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12} md={12}>
              {fileUp === '' ? (
                <UploadFile onDrop={handleDrop} />
              ) : (
                <Stack sx={{ m: 1 }}>
                  <Box
                    sx={{
                      m: 2,
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField
                      name="name"
                      label={i18n('patients.details.attachments.name', 'crs')}
                      error={!!errors?.name?.message}
                      helperText={errors?.name?.message}
                    />

                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          multiple
                          freeSolo
                          onChange={(event, newValue) => field.onChange(newValue)}
                          options={documentCategory?.asList().map((option) => option.display) || []}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                size="small"
                                label={option}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              label={i18n('patients.details.attachments.category', 'crs')}
                              {...params}
                            />
                          )}
                        />
                      )}
                    />
                    <RHFTextField
                      disabled
                      name="author"
                      label={i18n('patients.details.attachments.author', 'crs')}
                    />
                    <RHFSelect name="custodian" label="Custodian">
                      {organizations?.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                    <RHFTextField
                      disabled
                      name="fileType"
                      label={i18n('patients.details.attachments.fileType', 'crs')}
                    />
                    <RHFSelect name="type" label={i18n('patients.details.attachments.type', 'crs')}>
                      {documentType?.asList().map((option) => (
                        <MenuItem key={option.code} value={option.display}>
                          {option.display}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Box>

                  <Stack sx={{ m: 1 }}>
                    {fileUp && <Viewer file={fileUp} typeResource="blob" />}
                  </Stack>

                  <Stack spacing={2} alignItems="center">
                    <DialogActions>
                      <Box sx={{ flexGrow: 1 }} />

                      <CancelButton onClick={onClose}>{i18n('cancel')}</CancelButton>

                      <Button variant="contained" type="submit">
                        {i18n('saveChanges')}
                      </Button>
                    </DialogActions>
                  </Stack>
                </Stack>
              )}
            </Grid>
          </Grid>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
